import { useCallback } from "react";
import { ExtractedContract } from "../../types/contract";

export const useContractNavigation = (
  aggregatedContracts: ExtractedContract[],
  handleSelectContract: (contract: ExtractedContract | null, shouldScroll: boolean) => void
) => {
  const selectNextItem = useCallback((
    currentId: number,
    updatedConfirmed: Set<number>,
    updatedNeedsReview: Set<number>,
    shouldScroll: boolean // Add shouldScroll parameter
  ) => {
    if (aggregatedContracts.length === 0) {
      handleSelectContract(null, shouldScroll); // Use handleSelectContract
      return;
    }

    const findNext = (
      startIndex: number,
      predicate: (c: ExtractedContract) => boolean
    ): ExtractedContract | null => {
      // Search from startIndex to the end of the list
      for (let i = startIndex; i < aggregatedContracts.length; i++) {
        if (predicate(aggregatedContracts[i])) {
          return aggregatedContracts[i];
        }
      }
      // Wrap around and search from the beginning to startIndex
      for (let i = 0; i < startIndex; i++) {
        if (predicate(aggregatedContracts[i])) {
          return aggregatedContracts[i];
        }
      }
      return null;
    };

    const currentIndex = aggregatedContracts.findIndex((c) => c.id === currentId);
    // Start searching from the item *after* the current one
    const startSearchIndex = (currentIndex + 1) % aggregatedContracts.length;

    // Priority 1: Find the next unreviewed item (not confirmed and not needs review)
    let nextItem = findNext(
      startSearchIndex,
      (c) => !updatedConfirmed.has(c.id!) && !updatedNeedsReview.has(c.id!)
    );

    // Priority 2: If no unreviewed items, find the next "needs review" item
    if (!nextItem) {
      nextItem = findNext(startSearchIndex, (c) =>
        updatedNeedsReview.has(c.id!)
      );
    }

    // If after all searches, nextItem is still null:
    if (!nextItem) {
      // Check if this means ALL aggregatedContracts are now either confirmed or marked for review.
      // If all are confirmed, we don't want to set selectedContract to null,
      // we want to stay on the last item to prevent visual collapse.
      if (aggregatedContracts.length === updatedConfirmed.size) {
        return; // Keep the current selectedContract, do not set to null
      }
      // If nextItem is null but not all are confirmed (e.g., only needs review items left),
      // then we still need to set selectedContract to null to indicate no more unconfirmed.
    }
    handleSelectContract(nextItem, shouldScroll); // Use handleSelectContract
  }, [aggregatedContracts, handleSelectContract]);

  return {
    selectNextItem,
  };
};